import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface HolderDistributionChartProps {
  holderDistribution: Array<{
    name: string;
    value: number;
    walletCount: number;
    holdingsPercentage: number;
  }>;
  exchangeDistributionWarning?: string;
}

const COLORS = [
  '#4a3f8a', '#2d6b4f', '#8a6d2a', '#8a2e2e', '#2a5f6b',
  '#6b2d6b', '#7a4a1a', '#1f4f6b', '#5a2d5a', '#2d5a2d'
];

const HolderDistributionChart: React.FC<HolderDistributionChartProps> = ({ holderDistribution, exchangeDistributionWarning }) => {
  // Cap at 9 segments — roll the rest into "Other"
  const chartData = holderDistribution.length <= 10
    ? holderDistribution
    : [
        ...holderDistribution.slice(0, 9),
        {
          name: 'Other',
          value: holderDistribution.slice(9).reduce((sum, d) => sum + d.value, 0),
          walletCount: holderDistribution.slice(9).reduce((sum, d) => sum + d.walletCount, 0),
          holdingsPercentage: holderDistribution.slice(9).reduce((sum, d) => sum + d.holdingsPercentage, 0),
        },
      ];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-crypto-dark border border-gray-600 rounded-lg p-3 shadow-lg">
          <p className="text-white font-medium">{data.name}</p>
          <p className="text-gray-300">
            {`Wallet Count: ${data.walletCount}`}
          </p>
          <p className="text-gray-300">
            {`Holdings: ${data.holdingsPercentage.toFixed(2)}%`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <section className="mb-8">
      <div className="bg-crypto-gray border border-gray-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4 text-center">Funding Source Distribution</h3>
        {exchangeDistributionWarning && (
          <div className="mb-3 px-3 py-2 bg-yellow-900/30 border border-yellow-700/50 rounded-lg flex items-start gap-2">
            <i className="fas fa-exclamation-triangle text-yellow-400 mt-0.5 text-xs"></i>
            <span className="text-yellow-300 text-xs">{exchangeDistributionWarning}</span>
          </div>
        )}
        {chartData.length > 0 ? (
          // Layout: fixed-width pie + auto-sized legend grid, centered in
          // the card. Keeps the legend compact (swatch/name/value each
          // sized to content + small gap) so labels and their values
          // stay visually paired even when the outer container is wide.
          <div className="flex items-start gap-6 justify-center">
            <div className="flex-shrink-0" style={{ width: 180, height: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    isAnimationActive={false}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, holdingsPercentage }: any) => {
                      if (holdingsPercentage < 5) return null;
                      const RADIAN = Math.PI / 180;
                      const radius = innerRadius + (outerRadius - innerRadius) * 0.75;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      return (
                        <text x={x} y={y} fill="#e5e5e5" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}>
                          {`${holdingsPercentage.toFixed(0)}%`}
                        </text>
                      );
                    }}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="#888888" strokeWidth={1} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div
              className="pt-1 grid gap-x-4 gap-y-1 items-center"
              style={{ gridTemplateColumns: 'auto minmax(0, max-content) max-content' }}
            >
              {chartData.map((entry, index) => (
                <React.Fragment key={index}>
                  <span
                    className="w-2.5 h-2.5 rounded-sm"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-gray-300 text-xs truncate">{entry.name.split(' (')[0]}</span>
                  <span className="text-white font-medium text-xs text-right tabular-nums">{entry.holdingsPercentage.toFixed(0)}%</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-400 py-8">
            No distribution data available
          </div>
        )}
      </div>
    </section>
  );
};

export default HolderDistributionChart;
